import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowRight, CalendarTick, Headphone } from '@/components/icons';
import { Heading, PageSection, PageShell, Text } from '@/components/system';
import { SupportPage } from '@/pages/constants';
import { createPageUrl } from '@/utils';
import { findHelpArticle } from './articles';
import { ArticleBlocks } from './components/ArticleBlocks';
import { ArticleFeedback } from './components/ArticleFeedback';
import { HelpBreadcrumb } from './components/HelpBreadcrumb';
import { HELP_TOPICS } from './data/help-content';

export default function HelpArticlePage() {
    const { topicId, articleSlug } = useParams();
    const topic = HELP_TOPICS.find((item) => item.id === topicId);
    const article = topicId && articleSlug ? findHelpArticle(topicId, articleSlug) : undefined;

    useEffect(() => {
        if (article) {
            document.title = `LaudoPsi | ${article.title}`;
        }
    }, [article]);

    if (!topic || !article) {
        return <Navigate to={topic ? `/help/${topic.id}` : '/help'} replace />;
    }

    const subheadings = article.blocks.filter((block) => block.type === 'subheading');
    const updatedAtLabel = format(new Date(`${article.updatedAt}T12:00:00`), 'dd/MM/yyyy', {
        locale: ptBR,
    });

    return (
        <PageShell width="narrow">
            <HelpBreadcrumb
                items={[
                    { label: 'Central de ajuda', to: '/help' },
                    { label: topic.title, to: `/help/${topic.id}` },
                    { label: article.title },
                ]}
            />

            <article className="space-y-6">
                <header className="space-y-3">
                    <Heading as="h1" variant="page">
                        {article.title}
                    </Heading>
                    <Text variant="subtitle">{article.description}</Text>
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                        <CalendarTick className="size-3.5" />
                        <Text as="span" variant="caption">
                            Atualizado em {updatedAtLabel}
                        </Text>
                    </div>
                </header>

                {subheadings.length >= 2 && (
                    <nav
                        aria-label="Neste artigo"
                        className="rounded-xl border border-border/80 bg-surface-muted/50 p-4"
                    >
                        <Text variant="label" className="uppercase tracking-[0.12em]">
                            Neste artigo
                        </Text>
                        <ul className="mt-2 space-y-1.5">
                            {subheadings.map((item) => (
                                <li key={item.id}>
                                    <a
                                        href={`#${item.id}`}
                                        className="text-sm font-medium text-brand hover:underline"
                                    >
                                        {item.text}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>
                )}

                <PageSection contentClassName="p-5 sm:p-8">
                    <ArticleBlocks blocks={article.blocks} />
                </PageSection>

                <ArticleFeedback articleSlug={article.slug} />

                <Link
                    to={createPageUrl(SupportPage)}
                    className="group flex items-center gap-4 rounded-xl border border-border/80 bg-card p-4 shadow-sm transition-[background-color,border-color] duration-150 ease-out hover:border-border-strong hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                    <span className="flex size-11 shrink-0 items-center justify-center rounded-xl border border-info/20 bg-info-subtle text-info">
                        <Headphone className="size-5" />
                    </span>
                    <span className="min-w-0 flex-1 space-y-1">
                        <Heading as="h2" variant="card">
                            Não encontrou o que procurava?
                        </Heading>
                        <Text variant="description">
                            Abra um chamado e nossa equipe responde por e-mail.
                        </Text>
                    </span>
                    <ArrowRight className="size-4 shrink-0 text-muted-foreground transition-[color,transform] duration-150 ease-out group-hover:translate-x-0.5 group-hover:text-foreground" />
                </Link>
            </article>
        </PageShell>
    );
}
